import { useMemo } from 'react';
import { useBrandFeatures, type BrandFeature, type BrandFeatures } from './features';

/**
 * Which brand Feature each URL belongs to.
 *
 * `features.ts` says whether a product is granted. This file says which pages
 * *are* that product, which is what lets the router refuse a page whose feature
 * the super admin switched off — otherwise turning a product off only removed
 * the buttons and the page still answered to anyone typing the URL, or holding
 * an old bookmark.
 *
 * The paths MUST stay in step with `App.tsx`. Anything not listed has no
 * feature switch behind it and is always allowed: 404-ing a page the panel
 * gives no way to restore would be a change nobody could undo.
 */
export function featureForRoute(pathname: string): BrandFeature | undefined {
  const parts = pathname.toLowerCase().split('/').filter(Boolean);
  const [a, b, c] = parts;
  if (!a) return undefined;

  // --- Account lifecycle ----------------------------------------------------
  if (a === 'register') return 'registration';

  // --- Cashier --------------------------------------------------------------
  // One page per direction, so the two switches map cleanly onto two URLs.
  if (a === 'user') {
    if (b === 'deposit') return 'deposit';
    if (b === 'deposit-withdraw') return 'withdrawal';
    if (b === 'fd') return 'fd';
    return undefined;
  }

  // --- Sportsbook -----------------------------------------------------------
  // `/home` is deliberately absent: it is the landing page and doubles as the
  // shell for everything else, so it stays reachable with the odds table gone.
  if (a === 'inplay' || a === 'all-sports' || a === 'game-details') return 'sports';

  // --- Fantasy --------------------------------------------------------------
  if (a === 'fantasy-list') return 'fantasy';

  // --- Casino family --------------------------------------------------------
  // `/casino/:provider/:category` is one route standing in for the whole lobby;
  // the provider segment is a filter the player picks, so the category alone
  // decides. Lottery is the one category with its own switch.
  if (a === 'casino') {
    // `/casino/goal-game/:gameId` is a launched game, not a lobby category.
    if (b === 'goal-game') return 'casino';
    return c === 'lottery' ? 'lottery' : 'casino';
  }
  // `/qtech/casino/:provider/:category`
  if (a === 'qtech') return parts[3] === 'lottery' ? 'lottery' : 'casino';
  if (a === 'casino-playground' || a === 'casino-qtech') return 'casino';
  if (a === 'live-casino' || a === 'slots') return 'casino';

  return undefined;
}

/** Whether the router should refuse this URL for the current brand. */
export function isRouteFeatureOff(pathname: string, features: BrandFeatures): boolean {
  const feature = featureForRoute(pathname);
  return feature !== undefined && features[feature] === false;
}

/** Reactive form of {@link isRouteFeatureOff}, re-run when GET /brand lands. */
export function useIsRouteFeatureOff(pathname: string): boolean {
  const features = useBrandFeatures();
  return useMemo(() => isRouteFeatureOff(pathname, features), [pathname, features]);
}
